import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../../auth/AuthContext";
import { SignInButton } from "../../SignInButton";
import MCULogo from "../../../../assets/images/MCU_Logo.png";
import styles from "./HeaderV2.scss";

export function HeaderV2() {
  const auth = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);

    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const closeMenu = () => {
    setMobileMenuOpen(false);
  };

  const openAboutTab = (tab) => {
    closeMenu();
    window.dispatchEvent(new CustomEvent("openAboutTab", { detail: { tab } }));
  };

  const openContactModal = () => {
    closeMenu();
    window.dispatchEvent(new CustomEvent("openContactModal"));
  };

  // メニュー外をクリックしたら閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <header className={styles.headerWrapper}>
      <div className={styles.headerInner}>
        {/* ロゴエリア */}
        <div className={styles.logoArea}>
          <img src={MCULogo} alt="MetaCampUs" className={styles.metaCampUsLogo} />
        </div>

        {/* 余白（スペーサー） */}
        <div className={styles.spacer}></div>

        {/* 認証UI - デスクトップ用（テキスト付き） */}
        <div className={`${styles.authArea} ${styles.desktopOnly}`}>
          {auth.isSignedIn ? (
            <button
              onClick={() => auth.signOut()}
              className={styles.signOutButton}
            >
              ログアウト
            </button>
          ) : (
            <SignInButton />
          )}
        </div>

        {/* 認証UI - モバイル用（アイコンのみ） */}
        <div className={styles.mobileAuthIcon}>
          {auth.isSignedIn ? (
            <button
              onClick={() => auth.signOut()}
              className={styles.signOutIconButton}
              aria-label="ログアウト"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#E395B7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => { window.location.href = "/signin"; }}
              className={styles.signInIconButton}
              aria-label="サインイン"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#E395B7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
            </button>
          )}
        </div>

        {/* ハンバーガーメニューボタン（モバイル） */}
        <button
          ref={hamburgerRef}
          className={`${styles.hamburger} ${mobileMenuOpen ? styles.hamburgerOpen : ""}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="メニュー"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* ナビゲーション */}
        <nav
          ref={navRef}
          className={`${styles.nav} ${mobileMenuOpen ? styles.navOpen : ""}`}
          onClick={(e) => {
            // クリック要素がリンクやボタン以外の場合は閉じる
            if (e.target === e.currentTarget) {
              closeMenu();
            }
          }}
        >
          {/* 閉じるボタン */}
          <button
            className={styles.closeButton}
            onClick={closeMenu}
            aria-label="メニューを閉じる"
          >
            <span></span>
            <span></span>
          </button>

          {/* デスクトップ・モバイル共通 */}
          <button
            type="button"
            onClick={() => {
              closeMenu();
              setTimeout(() => {
                const heroSection = document.querySelector('[class*="hero-area"]');
                if (heroSection) {
                  heroSection.scrollIntoView({ behavior: "smooth", block: "start" });
                } else {
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                }
              }, 100);
            }}
            className={styles.navLink}
          >
            Home
          </button>
          <button type="button" onClick={() => scrollToSection("vision")} className={styles.navLink}>
            Vision
          </button>
          <button type="button" onClick={() => scrollToSection("worlds")} className={styles.navLink}>
            Worlds
          </button>
          <button type="button" onClick={() => scrollToSection("voice")} className={styles.navLink}>
            Voice
          </button>

          <button
            type="button"
            onClick={() => openAboutTab("terms")}
            className={`${styles.navLink} ${styles.mobileOnly}`}
          >
            利用規約
          </button>
          <button
            type="button"
            onClick={() => openAboutTab("faq")}
            className={`${styles.navLink} ${styles.mobileOnly}`}
          >
            よくある質問
          </button>
          <button
            type="button"
            onClick={() => openAboutTab("guide")}
            className={`${styles.navLink} ${styles.mobileOnly}`}
          >
            操作説明
          </button>
          <button
            type="button"
            onClick={openContactModal}
            className={`${styles.navLink} ${styles.mobileOnly}`}
          >
            お問い合わせ
          </button>

          {/* モバイル専用 認証ボタン（完全版） */}
          <div className={`${styles.mobileAuthButtons} ${styles.mobileOnly}`}>
            {auth.isSignedIn ? (
              <button
                onClick={() => {
                  closeMenu();
                  auth.signOut();
                }}
                className={styles.mobileSignOutButton}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                ログアウト
              </button>
            ) : (
              <button
                onClick={() => {
                  closeMenu();
                  window.location.href = "/signin";
                }}
                className={styles.mobileSignInButton}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                サインイン / サインアップ
              </button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
